"use client";

import { useApp } from "./AppProvider";
import type { WhyUsData } from "@/lib/cms";

interface WhyUsProps {
  data: WhyUsData;
}

export default function WhyUs({ data }: WhyUsProps) {
  const { isRTL } = useApp();
  const h = (en: string, ar: string) => isRTL ? (ar || en) : en;

  return (
    <section id="about">
      <div className="container">
        <div className="grid-2col" style={{ direction: isRTL ? "rtl" : "ltr" }}>
          {/* Visual */}
          <div style={{ position: "relative" }}>
            <div className="glass-card-strong" style={{ borderRadius: "28px", padding: "3rem", background: "linear-gradient(135deg, var(--badge-bg) 0%, rgba(113,53,140,0.08) 100%)", border: "1px solid rgba(227,1,125,0.2)", boxShadow: "0 30px 80px var(--shadow-glow)" }}>
              <div style={{ width: 200, height: 200, margin: "0 auto", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div className="animate-spin-slow" style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2px dashed rgba(227,1,125,0.4)" }} />
                <div style={{ width: 140, height: 140, borderRadius: "50%", background: "var(--gradient-brand)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "4rem", boxShadow: "0 0 60px rgba(227,1,125,0.5)" }}>🏆</div>
              </div>
              <div style={{ display: "flex", gap: "1rem", marginTop: "2rem", justifyContent: "center" }}>
                {data.stats.map((s) => (
                  <div key={s.label} style={{ textAlign: "center", padding: "0.8rem 1rem", background: "var(--stat-alt-bg)", borderRadius: "12px", flex: 1 }}>
                    <div style={{ fontWeight: 900, fontSize: "1.2rem", color: "var(--color-pink)" }}>{s.value}</div>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "0.2rem" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-card animate-float" style={{ position: "absolute", top: -20, right: isRTL ? undefined : -20, left: isRTL ? -20 : undefined, padding: "1rem 1.3rem", borderRadius: "16px", textAlign: "center", animationDelay: "1s" }}>
              <div style={{ fontSize: "1.8rem" }}>🌍</div>
              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "0.3rem" }}>Global Reach</div>
            </div>
          </div>

          {/* Text */}
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            <div className="section-badge"><span>✦</span> {h(data.badge_en, data.badge_ar)}</div>
            <h2 style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", fontWeight: 900, lineHeight: 1.15, letterSpacing: "-0.02em", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
              {h(data.title_en, data.title_ar)}
            </h2>
            <p style={{ color: "var(--text-secondary)", lineHeight: 1.8, marginBottom: "2rem", fontSize: "0.95rem" }}>
              {h(data.subtitle_en, data.subtitle_ar)}
            </p>
            {data.features.map((f) => (
              <div key={f.title_en} style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem", alignItems: "flex-start", flexDirection: isRTL ? "row-reverse" : "row" }}>
                <div style={{ width: 44, height: 44, background: "var(--icon-bg)", border: "1px solid var(--icon-border)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", flexShrink: 0 }}>
                  {f.icon}
                </div>
                <div>
                  <h4 style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: "0.3rem", color: "var(--text-primary)" }}>{h(f.title_en, f.title_ar)}</h4>
                  <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", lineHeight: 1.6 }}>{h(f.desc_en, f.desc_ar)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
